import { Badge, Card, CardContent, Skeleton } from "@roberttamas/ui";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { EmptyOrdersIllustration } from "@/components/illustrations";
import { OrderPackageTags } from "@/components/order-package-tags";
import { listMyOrders } from "@/lib/api";
import { formatDate } from "@/lib/locale-format";
import { formatAmount } from "@/lib/pricing";
import type { ListMyOrdersResp, OrderResp } from "@/lib/types";

function StatusBadge({ order }: { order: OrderResp }) {
  const { t } = useTranslation();
  if (order.status === "paid") {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600">
        {t("orderStatus.paid")}
      </Badge>
    );
  }
  if (order.status === "cancelled") {
    return <Badge variant="outline">{t("orderStatus.cancelled")}</Badge>;
  }
  return <Badge variant="outline">{t("orderStatus.awaitingPayment")}</Badge>;
}

/**
 * Buyer-facing order overview — the post-login home for non-admin
 * users (AdminRoute forwards them here). Each row links to the
 * order's QR/payment page.
 */
export function MyOrdersPage() {
  const { t } = useTranslation();
  const query = useQuery<ListMyOrdersResp>({
    queryKey: ["myOrders"],
    queryFn: listMyOrders,
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <p className="text-sm text-destructive">{t("myOrders.loadFailed")}</p>
    );
  }

  const orders = query.data?.orders ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("myOrders.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("myOrders.subtitle")}</p>
      </div>

      {orders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <EmptyOrdersIllustration className="h-60 w-60" />
            <p className="text-sm text-muted-foreground">
              <Trans
                i18nKey="myOrders.emptyPrompt"
                components={{
                  l: (
                    <Link
                      to="/buy"
                      className="font-medium text-foreground underline underline-offset-4"
                    />
                  ),
                }}
              />
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const needsSetup = order.status === "paid" && !order.siteId;
            return (
              <Link key={order.orderId} to={`/my-orders/${order.orderId}`} className="block">
                <Card className="transition-colors hover:bg-muted/50">
                  <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <p className="truncate font-medium">{order.snapshot.tierName}</p>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <OrderPackageTags snapshot={order.snapshot} />
                        <span className="text-sm text-muted-foreground">
                          {formatAmount(order.snapshot.amountMinor, order.snapshot.currency)}
                          {order.createdAt
                            ? `, ${t("myOrders.purchased", { date: formatDate(new Date(order.createdAt)) })}`
                            : ""}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge order={order} />
                      {needsSetup && (
                        <Badge className="border-primary/40 bg-primary/10 text-primary hover:bg-primary/10">
                          {t("myOrders.setupBadge")}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
